import React, { useEffect, useState } from "react";
import api, {API, authFetch } from "@/lib/api";
import QRBox from "@/components/QRBox";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { nonAncoraAssunto, daQuando, inUfficio, bloccatoDaPratica,
         cosaMancaPratica } from "@/lib/disponibili";
import PraticaAssunzione from "@/components/PraticaAssunzione";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit, Trash2, QrCode, FileText, Search, ExternalLink, Upload, Archive, IdCard, GraduationCap, Clock, Building2, UploadCloud, ScrollText, FileSpreadsheet, HardHat, PenLine, ChevronDown, Download, X } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { puoGestire } from "@/lib/ruoli";
import { DEFAULT_EMPLOYEE_PHOTO, photoUrl, fileUrl } from "@/lib/placeholders";
import ImportDocumentiDialog from "@/components/ImportDocumentiDialog";
import { IconeFigure } from "@/components/FigureSicurezza";
import PuliziaFascicoliDialog from "@/components/PuliziaFascicoliDialog";
import BottoneRaccogli, { BottoneRaccogliTutti } from "@/components/BottoneRaccogli";
import BottoneFoto from "@/components/BottoneFoto";

/* ---------- Tassonomia documenti ---------- */
export const PERSONAL_DOC_TYPES = [
  "Carta d'identità", "Codice fiscale", "Patente di guida", "UNILAV",
  "Idoneità Sanitarie", "Permesso di soggiorno", "Consegna DPI", "Altro documento",
];

export const COURSE_TYPES = [
  // Le nomine delle figure previste dal D.Lgs. 81/08 - RSPP, RLS, medico
  // competente, preposti, addetti - si archiviano qui con la loro scadenza,
  // cosi' rientrano nei controlli come tutti gli altri documenti.
  "Figure D.Lgs. 81/08",
  "Antincendio", "Primo Soccorso", "Preposti", "Dirigenti", "RSPP/ASPP", "RLS",
  "Formazione generale", "Formazione specifica", "Operatori MMT",
  "PLE (piattaforme aeree)", "Carrello elevatore", "Gru su autocarro",
  "Spazi confinati", "Lavori in quota / DPI III cat.", "Rischio elettrico PES-PAV-PEI",
  "Patentino Saldatura ACC", "Patentino Saldatura PEAD", "Segnaletica Stradale",
  "Pronto Intervento Gas", "Stop System", "Cercafughe", "Esplosimetro",
  "Altro corso",
];

export const MANSIONI = [
  "Titolare", "Impiegato Tecnico", "Impiegato Amministrativo", "Operaio Qualificato",
  "Operaio Specializzato", "Operatore MMT", "Operatore Conduzione Mezzi",
  // I mestieri per cui esistono i turni di reperibilita': scritti cosi' -
  // «Autista», «Escavatorista», «Saldatore» - l'app riconosce chi si puo'
  // chiamare per quel turno.
  "Autista", "Escavatorista", "Saldatore", "Giuntista", "Magazziniere",
];

const TIPOLOGIE = ["Prima emissione", "Aggiornamento"];
const ORE_PRESET = [4, 8, 12, 16, 24, 32, 40];

// Compatibilità con i dati già presenti: se doc_class manca lo deduce dalla categoria
export const classOf = (c) =>
  c.doc_class ? c.doc_class : (PERSONAL_DOC_TYPES.includes(c.category) ? "personale" : "attestato");

// Il tesserino di cantiere richiede per legge generalita e data di assunzione.
// Il titolare pero' non e' un assunto: una data di assunzione non ce l'ha, e
// pretenderla lo faceva risultare per sempre incompleto.
const MANSIONI_SENZA_ASSUNZIONE = ["Titolare"];

/**
 * Tutti i mestieri di una persona: la mansione principale e le altre.
 *
 * Una persona sola puo' saper fare piu' cose - l'autista che guida anche
 * l'escavatore - e dove conta saper fare qualcosa valgono tutte. Dove invece
 * conta l'inquadramento - il tesserino, l'elenco per il committente - si
 * guarda la sola mansione principale, che e' quella contrattuale.
 */
export const mansioniDi = (emp) => {
  const tutte = [emp?.role, ...(emp?.mansioni_extra || [])]
    .map((m) => String(m || "").trim()).filter(Boolean);
  return [...new Set(tutte)];
};

const datiTesserinoMancanti = (emp) => {
  const mancanti = [];
  if (!emp.birth_date) mancanti.push("data di nascita");
  if (!emp.hire_date && !MANSIONI_SENZA_ASSUNZIONE.includes(emp.role)) {
    mancanti.push("data di assunzione");
  }
  return mancanti.join(" e la ");
};

const dayDiff = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return Math.ceil((d - new Date()) / 86400000);
};

export default function Dipendenti() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Anagrafica, foto, archiviazione e import sono `require_gestione` sul
  // server: l'amministrazione ci lavora quanto la direzione. Restano
  // all'amministratore la pulizia dei fascicoli e il registro.
  const gestisce = puoGestire(user);
  // L'elenco del personale contiene dati anagrafici: lo scarica solo chi gestisce
  const puoEsportare = user?.role === "admin" || user?.role === "amministrativo";
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  const [mansioniFiltro, setMansioniFiltro] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [qrEmp, setQrEmp] = useState(null);
  const [certEmp, setCertEmp] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showHireDates, setShowHireDates] = useState(false);
  const [showPulizia, setShowPulizia] = useState(false);
  const [tesserino, setTesserino] = useState(null);
  // La pratica di assunzione aperta: chi ha un vincolo da superare prima di
  // poter andare in cantiere.
  const [praticaEmp, setPraticaEmp] = useState(null);
  const praticheAperte = list.filter(e => bloccatoDaPratica(e)).length;
  const missingHireDates = list.filter(
    e => !e.hire_date && !MANSIONI_SENZA_ASSUNZIONE.includes(e.role)).length;
  const senzaDatiTesserino = list.filter(e => datiTesserinoMancanti(e)).length;

  const load = async () => {
    try {
      const { data } = await api.get("/employees");
      setList(data);
    } catch { toast.error("Errore caricamento dipendenti"); }
  };
  useEffect(() => { load(); }, []);

  // Arrivo dallo scanner QR: apre subito il fascicolo del tesserino inquadrato.
  const [searchParams, setSearchParams] = useSearchParams();
  const wantedId = searchParams.get("fascicolo");
  useEffect(() => {
    if (!wantedId || list.length === 0) return;
    const emp = list.find(e => e.id === wantedId);
    if (emp) setCertEmp(emp);
    else toast.error("Tesserino non riconosciuto: dipendente non trovato");
    searchParams.delete("fascicolo");
    setSearchParams(searchParams, { replace: true });
  }, [wantedId, list, searchParams, setSearchParams]);

  const filtered = list.filter(e =>
    (mansioniFiltro.length === 0
      || mansioniDi(e).some((m) => mansioniFiltro.includes(m))) &&
    (e.full_name.toLowerCase().includes(filter.toLowerCase()) ||
     (e.badge_number || "").toLowerCase().includes(filter.toLowerCase())));

  // Pastiglie delle mansioni: solo quelle davvero presenti, nell'ordine
  // dell'anagrafica. Una persona con due mestieri si conta in tutti e due,
  // perche' la pastiglia risponde a «chi mi sa fare questo».
  const conteggioMansioni = list.reduce((acc, e) => {
    for (const m of mansioniDi(e)) acc[m] = (acc[m] || 0) + 1;
    return acc;
  }, {});
  const mansioniPresenti = Object.keys(conteggioMansioni).sort((a, b) =>
    ((MANSIONI.indexOf(a) + 1) || 99) - ((MANSIONI.indexOf(b) + 1) || 99) || a.localeCompare(b));
  const toggleMansione = (m) => setMansioniFiltro(prev =>
    prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);

  /**
   * Le etichette dei tesserini per la stampante a nastro.
   *
   * Esce un archivio con un'immagine per persona, gia' della misura esatta:
   * nel programma della Brother si inserisce e si stampa, senza impaginare
   * niente. Vale l'elenco che si sta guardando, filtri compresi.
   */
  /**
   * Le foto dei tesserini, una per persona, con l'elenco in Excel.
   *
   * Serve quando un committente chiede l'anagrafica del personale con le
   * fotografie: ogni foto esce sotto i cento kilobyte e si chiama con
   * cognome e nome, non con il codice interno.
   */
  const [fotoZip, setFotoZip] = useState(false);
  const scaricaFoto = async () => {
    setFotoZip(true);
    try {
      const p = new URLSearchParams();
      if (filter.trim()) p.set("q", filter.trim());
      mansioniFiltro.forEach((m) => p.append("mansione", m));
      const qs = p.toString();
      const r = await authFetch(`/employees/export-foto${qs ? `?${qs}` : ""}`);
      if (!r.ok) {
        let dettaglio = "Errore nella preparazione delle foto";
        try { dettaglio = (await r.json()).detail || dettaglio; } catch { /* niente */ }
        throw new Error(dettaglio);
      }
      const cd = r.headers.get("content-disposition") || "";
      const nome = /filename="?([^";]+)"?/.exec(cd)?.[1] || "foto_dipendenti_SGM.zip";
      const quante = r.headers.get("X-Foto");
      const senza = Number(r.headers.get("X-Senza-Foto") || 0);
      const u = window.URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = u; a.download = nome; a.click();
      window.URL.revokeObjectURL(u);
      toast.success(`${quante} foto pronte`, {
        description: senza
          ? `${senza} senza fotografia: l'elenco Excel dice chi sono.`
          : "Nell'archivio c'è anche l'elenco in Excel.",
      });
    } catch (e) { toast.error(e.message || "Errore"); }
    finally { setFotoZip(false); }
  };

  const [etichette, setEtichette] = useState(false);
  const scaricaEtichette = async () => {
    setEtichette(true);
    try {
      const p = new URLSearchParams();
      if (filter.trim()) p.set("q", filter.trim());
      mansioniFiltro.forEach(m => p.append("mansione", m));
      const qs = p.toString();
      const r = await authFetch(`/employees/export-etichette${qs ? `?${qs}` : ""}`);
      if (!r.ok) {
        let dettaglio = "Errore nella preparazione delle etichette";
        try { dettaglio = (await r.json()).detail || dettaglio; } catch { /* niente */ }
        throw new Error(dettaglio);
      }
      const cd = r.headers.get("content-disposition") || "";
      const nome = /filename="?([^";]+)"?/.exec(cd)?.[1] || "etichette_badge_SGM.zip";
      const quante = r.headers.get("X-Etichette");
      const fuori = Number(r.headers.get("X-Saltati") || 0);
      const u = window.URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = u; a.download = nome; a.click();
      window.URL.revokeObjectURL(u);
      toast.success(`${quante} tesserini pronti da stampare`, {
        description: fuori
          ? `${fuori} rimasti fuori: senza data di nascita o di assunzione il tesserino non è a norma. L'elenco è nel LEGGIMI.`
          : "Nell'archivio c'è anche il LEGGIMI con le istruzioni per la stampante.",
      });
    } catch (e) { toast.error(e.message || "Errore"); }
    finally { setEtichette(false); }
  };

  // Esporta esattamente l'elenco che si sta guardando, filtri compresi
  const esportaElenco = async () => {
    try {
      const p = new URLSearchParams();
      if (filter.trim()) p.set("q", filter.trim());
      mansioniFiltro.forEach(m => p.append("mansione", m));
      const qs = p.toString();
      const r = await authFetch(`/employees/export-elenco${qs ? `?${qs}` : ""}`);
      if (!r.ok) throw new Error();
      const cd = r.headers.get("content-disposition") || "";
      const nome = /filename="?([^";]+)"?/.exec(cd)?.[1] || "elenco_personale_SGM.xlsx";
      const u = window.URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = u; a.download = nome; a.click();
      window.URL.revokeObjectURL(u);
      toast.success("Elenco scaricato");
    } catch { toast.error("Errore nel download dell'elenco"); }
  };

  const submit = async (form) => {
    try {
      let nuovo = null;
      if (editing) await api.put(`/employees/${editing.id}`, form);
      else nuovo = (await api.post("/employees", form)).data;
      toast.success("Salvato");
      setShowForm(false); setEditing(null);
      await load();
      // L'anagrafica e' solo il primo passo: subito dopo si apre la pratica,
      // perche' e' quella che permette di mandarlo in cantiere. Se la persona
      // non e' soggetta - assunta prima che la pratica esistesse - la finestra
      // lo dice e si chiude.
      if (nuovo) setPraticaEmp(nuovo);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const onDelete = async (emp) => {
    if (!window.confirm(
      `Spostare ${emp.full_name} nell'archivio ex dipendenti?\n\n` +
      `La scheda e tutti i documenti restano conservati e consultabili dall'amministratore.`
    )) return;
    try {
      await api.delete(`/employees/${emp.id}`);
      toast.success("Dipendente archiviato");
      load();
    } catch { toast.error("Errore"); }
  };

  const apriPdf = async (percorso, errore) => {
    const r = await authFetch(percorso);
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.detail || errore);
    }
    const u = window.URL.createObjectURL(await r.blob());
    window.open(u, "_blank");
    setTimeout(() => window.URL.revokeObjectURL(u), 60000);
  };

  // Quanti tesserini si consegnano lo si chiede una volta sola: lo stesso
  // numero va stampato e scritto sul verbale che il lavoratore firma.
  const printBadge = (emp) => setTesserino(emp);

  const stampaTesserino = async (emp, copie, ancheVerbale) => {
    try {
      await apriPdf(`/employees/${emp.id}/badge-pdf?copie=${copie}`,
                    "Errore nella generazione del tesserino");
      if (ancheVerbale) {
        await apriPdf(`/employees/${emp.id}/verbale-tesserino-pdf?copie=${copie}`,
                      "Errore nella generazione del verbale");
      }
      setTesserino(null);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-6" data-testid="dipendenti-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">Dipendenti</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Elenco e fascicoli</h1>
          <p className="text-sm text-slate-500 mt-1">
            {filtered.length === list.length
              ? `${list.length} attivi`
              : `${filtered.length} di ${list.length} attivi`}
            {senzaDatiTesserino > 0 && (
              <span className="text-orange-600">
                {" · "}{senzaDatiTesserino} senza i dati per il tesserino
              </span>
            )}
            {praticheAperte > 0 && (
              <span className="text-red-600">
                {" · "}{praticheAperte === 1
                  ? "1 pratica di assunzione da concludere"
                  : `${praticheAperte} pratiche di assunzione da concludere`}
              </span>
            )}
          </p>
        </div>
        {gestisce && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" className="h-12" onClick={() => setShowImport(true)}
                    data-testid="import-docs-btn">
              <UploadCloud className="w-4 h-4 mr-2" /> Import massivo
            </Button>
            <Button variant="outline" className="h-12" onClick={() => setShowHireDates(true)}
                    data-testid="dati-anagrafici-btn">
              <IdCard className="w-4 h-4 mr-2" /> Dati anagrafici
              {missingHireDates > 0 && (
                <Badge variant="outline" className="ml-2 text-orange-600 border-orange-300">
                  {missingHireDates}
                </Badge>
              )}
            </Button>
            {isAdmin && (
              <Button variant="outline" className="h-12 text-red-600" onClick={() => setShowPulizia(true)}
                      data-testid="pulizia-fascicoli-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Pulizia fascicoli
              </Button>
            )}
            {isAdmin && (
              <Link to="/registro">
                <Button variant="outline" className="h-12" data-testid="registro-btn">
                  <ScrollText className="w-4 h-4 mr-2" /> Registro
                </Button>
              </Link>
            )}
            <Link to="/archivio-dipendenti">
              <Button variant="outline" className="h-12" data-testid="archivio-btn">
                <Archive className="w-4 h-4 mr-2" /> Archivio
              </Button>
            </Link>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white h-12"
                    onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-emp-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuovo dipendente
            </Button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Cerca per nome o badge..." className="pl-9 h-11"
                 value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="emp-search" />
        </div>
        {puoEsportare && (
          <Button variant="outline" className="h-11" onClick={esportaElenco}
                  title="Scarica in Excel l'elenco che stai guardando"
                  data-testid="export-personale-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Export elenco
          </Button>
        )}
        {puoEsportare && (
          <Button variant="outline" className="h-11" onClick={scaricaFoto}
                  disabled={fotoZip}
                  title="Le fotografie dei tesserini, con l'elenco in Excel"
                  data-testid="export-foto-btn">
            <Download className="w-4 h-4 mr-2" />
            {fotoZip ? "Preparo..." : "Foto e elenco"}
          </Button>
        )}
        {puoEsportare && (
          <Button variant="outline" className="h-11" onClick={scaricaEtichette}
                  disabled={etichette}
                  title="Le etichette dei tesserini per la stampante a nastro Brother"
                  data-testid="etichette-badge-btn">
            <IdCard className="w-4 h-4 mr-2" />
            {etichette ? "Preparo..." : "Etichette badge"}
          </Button>
        )}
      </div>

      {/* Filtro per mansione: si sommano fra loro, come i committenti in pianificazione */}
      {mansioniPresenti.length > 1 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {mansioniPresenti.map(m => {
            const attivo = mansioniFiltro.includes(m);
            return (
              <button key={m || "senza"} type="button" data-testid={`mansione-${m || "senza"}`}
                      onClick={() => toggleMansione(m)}
                      title={`${conteggioMansioni[m]} in questa mansione`}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs
                                  transition-colors ${attivo
                                    ? "border-slate-900 bg-slate-900 text-white"
                                    : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                <span className="truncate max-w-[180px]">{m || "Senza mansione"}</span>
                <span className={attivo ? "text-slate-300" : "text-slate-400"}>{conteggioMansioni[m]}</span>
              </button>
            );
          })}
          {mansioniFiltro.length > 0 && (
            <button type="button" onClick={() => setMansioniFiltro([])}
                    data-testid="mansioni-azzera"
                    className="px-2.5 py-1 rounded-full border border-orange-300 text-orange-700
                               text-xs hover:bg-orange-50">
              mostra tutte
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(emp => (
          <Card key={emp.id} className="border-slate-200 hover:shadow-md transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-start gap-3 mb-2">
                {gestisce ? (
                  <BottoneFoto url={`/employees/${emp.id}/photo`} onCaricata={load}
                               titolo="Cambia la foto" classeContenitore="flex-shrink-0"
                               testId={`foto-img-${emp.id}`}>
                    <img src={photoUrl(emp.photo_url) || DEFAULT_EMPLOYEE_PHOTO} alt={emp.full_name}
                         className="w-16 h-16 rounded-full object-cover border-2 border-slate-100" />
                  </BottoneFoto>
                ) : (
                  <img src={photoUrl(emp.photo_url) || DEFAULT_EMPLOYEE_PHOTO} alt={emp.full_name}
                       className="w-16 h-16 rounded-full object-cover border-2 border-slate-100 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-xs text-slate-500 font-mono">{emp.badge_number}</div>
                    <IconeFigure figure={emp.figure_sicurezza} />
                  </div>
                  <div className="font-display font-bold text-lg leading-tight">{emp.full_name}</div>
                  {inUfficio(emp) && (
                    <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px] mt-0.5"
                           title="Account tecnico o amministrativo: non compare fra chi si manda in cantiere">
                      ufficio
                    </Badge>
                  )}
                  {nonAncoraAssunto(emp) && (
                    <Badge className="bg-sky-100 text-sky-800 border-0 text-[10px] mt-0.5"
                           title="Entra in azienda più avanti: non compare fra chi si può assegnare">
                      entra {daQuando(emp)}
                    </Badge>
                  )}
                  {bloccatoDaPratica(emp) && (
                    <Badge className="bg-red-100 text-red-700 border-0 text-[10px] mt-0.5"
                           title="Pratica di assunzione da concludere: in cantiere non ci va">
                      pratica aperta{cosaMancaPratica(emp) && ` · manca ${cosaMancaPratica(emp)}`}
                    </Badge>
                  )}
                  {emp.pratica?.deroga && (
                    <Badge className="bg-purple-100 text-purple-700 border-0 text-[10px] mt-0.5"
                           title="Assegnabile per deroga dell'amministratore">
                      in deroga
                    </Badge>
                  )}
                  {emp.pratica?.chiusa && emp.pratica.in_attesa?.length > 0 && (
                    <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px] mt-0.5"
                           title="Pratica conclusa, ma un documento deve ancora arrivare">
                      documenti in arrivo
                    </Badge>
                  )}
                  <div className="flex flex-wrap items-center gap-1 mt-1">
                    <Badge variant="outline">{emp.role}</Badge>
                    {mansioniDi(emp).slice(1).map((m) => (
                      <Badge key={m} variant="outline"
                             className="border-dashed text-slate-500"
                             title="Altra mansione: vale per i turni e per i filtri">
                        {m}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
              <div className="text-xs text-slate-500 mt-2 space-y-1">
                {emp.email && <div className="truncate">{emp.email}</div>}
                {emp.phone && <div>{emp.phone}</div>}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button variant="outline" size="sm" onClick={() => setQrEmp(emp)} data-testid={`qr-${emp.id}`}>
                  <QrCode className="w-3 h-3 mr-1" /> QR
                </Button>
                <Button variant="outline" size="sm" onClick={() => setCertEmp(emp)} data-testid={`fascicolo-${emp.id}`}>
                  <FileText className="w-3 h-3 mr-1" /> Fascicolo
                </Button>
                {/* La pratica la compila e la chiude l'amministrazione: al
                    tecnico il pulsante non si mostra, perche' il server gli
                    risponderebbe 403. Lo stato lo vede comunque dalla
                    pastiglia rossa e dagli elenchi di assegnazione. */}
                {emp.pratica && gestisce && (
                  <Button variant="outline" size="sm" onClick={() => setPraticaEmp(emp)}
                          data-testid={`pratica-${emp.id}`}
                          className={bloccatoDaPratica(emp) ? "text-red-600 border-red-200" : ""}>
                    <HardHat className="w-3 h-3 mr-1" /> Pratica
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => printBadge(emp)}
                        title={datiTesserinoMancanti(emp)
                               ? `Manca la ${datiTesserinoMancanti(emp)}`
                               : "Stampa il tesserino di cantiere"}
                        className={datiTesserinoMancanti(emp) ? "text-slate-400" : ""}
                        data-testid={`tesserino-${emp.id}`}>
                  <IdCard className="w-3 h-3 mr-1" /> Tesserino
                </Button>
                <Link to={`/fascicolo/${emp.id}`} target="_blank">
                  <Button variant="outline" size="sm"><ExternalLink className="w-3 h-3" /></Button>
                </Link>
                {gestisce && <>
                  <Button variant="outline" size="sm" onClick={() => { setEditing(emp); setShowForm(true); }}>
                    <Edit className="w-3 h-3" />
                  </Button>
                  <BottoneFoto url={`/employees/${emp.id}/photo`} onCaricata={load}
                               etichetta="Foto" testId={`foto-${emp.id}`} />
                  <Button variant="outline" size="sm" onClick={() => onDelete(emp)} title="Archivia dipendente">
                    <Trash2 className="w-3 h-3 text-red-500" />
                  </Button>
                </>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {showForm && <EmpForm initial={editing} onSubmit={submit}
                            onClose={() => { setShowForm(false); setEditing(null); }} />}
      {showImport && <ImportDocumentiDialog onClose={() => setShowImport(false)} onDone={load} />}
      {showHireDates && <DatiAnagraficiDialog onClose={() => setShowHireDates(false)} onDone={load} />}
      {showPulizia && <PuliziaFascicoliDialog onClose={() => setShowPulizia(false)} onDone={load} />}
      {qrEmp && <QRDialog emp={qrEmp} onClose={() => setQrEmp(null)} />}
      {certEmp && <FascicoloDialog emp={certEmp} onClose={() => setCertEmp(null)} />}
      {praticaEmp && (
        <PraticaAssunzione emp={praticaEmp} isAdmin={isAdmin}
                           onChiudi={() => setPraticaEmp(null)}
                           onCambio={load} />
      )}
      {tesserino && <StampaTesserinoDialog emp={tesserino} onStampa={stampaTesserino}
                                           onClose={() => setTesserino(null)} />}
    </div>
  );
}

// L'ufficio ha gia' l'elenco con email, nascita, residenza, codice fiscale,
// livello e assunzione: si carica quello. Prima si vede riga per riga cosa
// cambierebbe, poi si scrive. Una cella vuota non cancella niente.
function DatiAnagraficiDialog({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [esito, setEsito] = useState(null);
  const [soloCambi, setSoloCambi] = useState(true);

  const scaricaModello = async () => {
    try {
      const r = await authFetch("/employees/anagrafica-template");
      if (!r.ok) throw new Error();
      const u = window.URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = u; a.download = "dati_anagrafici_dipendenti_SGM.xlsx"; a.click();
      window.URL.revokeObjectURL(u);
    } catch { toast.error("Errore nel download del modello"); }
  };

  const manda = async (soloVerifica) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("excel_file", file);
      fd.append("solo_verifica", soloVerifica ? "true" : "false");
      const { data } = await api.post("/employees/anagrafica-import", fd);
      setEsito(data);
      if (!soloVerifica) {
        toast.success(`${data.aggiornati} schede aggiornate`);
        onDone();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel caricamento");
    } finally { setBusy(false); }
  };

  const verificato = esito && esito.solo_verifica;
  const STATO = {
    da_aggiornare: ["da aggiornare", "bg-emerald-50 text-emerald-700"],
    invariato: ["già uguale", "bg-slate-100 text-slate-500"],
    non_riconosciuto: ["non riconosciuto", "bg-red-50 text-red-700"],
    errori: ["errori", "bg-amber-50 text-amber-800"],
    doppia: ["riga doppia", "bg-slate-100 text-slate-500"],
  };
  const righe = (esito?.righe || []).filter((r) => !soloCambi || r.stato !== "invariato");
  const giorno = (v) => (v && /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10).split("-").reverse().join("/") : v);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Dati anagrafici dei dipendenti</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Carica l&apos;elenco dell&apos;ufficio: nome e cognome, email, data e comune di nascita,
            permesso di soggiorno, indirizzo, comune, provincia, nazione, codice fiscale, livello,
            data di assunzione. Le colonne si riconoscono dal titolo e l&apos;ordine non conta.
            Ogni dipendente si riconosce dal nome; una cella vuota non cancella niente.
          </p>
          <div className="flex flex-wrap gap-3">
            <div className="rounded border border-slate-200 p-3 space-y-2 flex-1 min-w-[220px]">
              <div className="text-sm font-medium">Hai già l&apos;elenco? Caricalo</div>
              <Input type="file" accept=".xlsx,.xlsm" onChange={(e) => { setFile(e.target.files[0]); setEsito(null); }} />
            </div>
            <div className="rounded border border-slate-200 p-3 space-y-2 min-w-[220px]">
              <div className="text-sm font-medium">Oppure parti dal foglio già compilato</div>
              <Button variant="outline" size="sm" onClick={scaricaModello}>
                <FileText className="w-3 h-3 mr-1" /> Scarica Excel
              </Button>
            </div>
          </div>

          {esito && (
            <div className="space-y-2 text-sm">
              <div className="flex flex-wrap gap-2 text-xs">
                {Object.entries(esito.conteggi).map(([k, v]) => (
                  <span key={k} className={`px-2 py-0.5 rounded-full ${STATO[k]?.[1] || ""}`}>
                    {STATO[k]?.[0] || k}: <strong>{v}</strong>
                  </span>
                ))}
                <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                  campi da scrivere: <strong>{esito.campi_da_scrivere}</strong>
                </span>
              </div>
              <p className="text-xs text-slate-500">
                Foglio «{esito.foglio}» · colonne lette: {esito.colonne_lette.join(", ")}.
                {esito.colonne_ignorate.length > 0 && <> Non trovate: {esito.colonne_ignorate.join(", ")}.</>}
              </p>
              {esito.non_nel_foglio.length > 0 && (
                <p className="text-xs text-amber-700">
                  In elenco ma non nel foglio: {esito.non_nel_foglio.join(", ")}.
                </p>
              )}
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={soloCambi} onChange={(e) => setSoloCambi(e.target.checked)} />
                nascondi le righe già uguali
              </label>
              <div className="overflow-x-auto border border-slate-200 rounded">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>{["Riga", "Nel foglio", "Dipendente", "Cosa cambia", "Esito"].map((c) => (
                      <th key={c} className="text-left px-2 py-1">{c}</th>))}</tr>
                  </thead>
                  <tbody>
                    {righe.map((r) => (
                      <tr key={r.riga} className="border-t border-slate-100 align-top">
                        <td className="px-2 py-1 text-slate-400">{r.riga}</td>
                        <td className="px-2 py-1">{r.nome}</td>
                        <td className="px-2 py-1">{r.dipendente || "—"}</td>
                        <td className="px-2 py-1">
                          {r.modifiche.map((m) => (
                            <div key={m.campo}>
                              <span className="text-slate-500">{m.etichetta}:</span>{" "}
                              {m.da ? <><s className="text-slate-400">{giorno(m.da)}</s> → </> : null}
                              <strong>{giorno(m.a)}</strong>
                            </div>
                          ))}
                          {r.errori.map((e, i) => <div key={i} className="text-red-700">{e}</div>)}
                        </td>
                        <td className="px-2 py-1">
                          <span className={`px-1.5 py-0.5 rounded whitespace-nowrap ${STATO[r.stato]?.[1]}`}>{STATO[r.stato]?.[0] || r.stato}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Chiudi</Button>
          {!verificato ? (
            <Button onClick={() => manda(true)} disabled={!file || busy} data-testid="verifica-anagrafica">
              {busy ? "Leggo..." : "Verifica"}
            </Button>
          ) : (
            <Button onClick={() => manda(false)} disabled={busy || !esito.campi_da_scrivere}
                    className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="carica-anagrafica">
              {busy ? "Scrivo..." : `Aggiorna ${esito.conteggi.da_aggiornare || 0} schede`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Quanti tesserini si stampano e se serve anche il verbale: si chiede prima,
// perche' il numero dev'essere lo stesso sui due documenti.
function StampaTesserinoDialog({ emp, onStampa, onClose }) {
  const [copie, setCopie] = useState(2);
  const [ancheVerbale, setAncheVerbale] = useState(true);
  const [busy, setBusy] = useState(false);

  const stampa = async () => {
    setBusy(true);
    try { await onStampa(emp, copie, ancheVerbale); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Stampa del tesserino</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm">
            Tesserino di <span className="font-semibold">{emp.full_name}</span>.
          </p>
          <div>
            <Label>Quanti tesserini consegni</Label>
            <select className="w-full h-10 border border-slate-200 rounded px-3 mt-1"
                    value={copie} onChange={(e) => setCopie(Number(e.target.value))}
                    data-testid="copie-tesserino">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <option key={n} value={n}>{n}{n === 2 ? " (consueto)" : ""}</option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">
              Se ne consegnano due: uno da tenere addosso e uno di scorta.
            </p>
          </div>
          <label className="flex items-start gap-2 text-sm cursor-pointer bg-slate-50 border border-slate-200 rounded p-2.5">
            <input type="checkbox" checked={ancheVerbale} className="mt-0.5"
                   onChange={(e) => setAncheVerbale(e.target.checked)}
                   data-testid="anche-verbale" />
            <span>
              Stampa anche il <span className="font-medium">verbale di consegna</span>,
              quello che il lavoratore firma per ricevuta, con i riferimenti di legge
              in calce.
            </span>
          </label>
          {ancheVerbale && !emp.birth_place && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              Manca il luogo di nascita: il verbale esce con la riga in bianco da
              completare a penna. Puoi aggiungerlo dalla scheda del dipendente.
            </p>
          )}
        </div>
        {/* Il tesserino e il verbale si stampano, ma qualche volta vanno
            mandati al committente insieme al resto: da qui entrano nel
            raccoglitore come gli altri documenti. */}
        <div className="flex items-center gap-2 border-t border-slate-100 pt-3">
          <span className="text-xs text-slate-500 flex-1">
            Oppure mettili nel raccoglitore, per mandarli insieme agli altri
            documenti:
          </span>
          <BottoneRaccogli className="h-8 w-8 p-0"
                           voce={{ tipo: "generato", id: `tesserino:${emp.id}`,
                                   gruppo: emp.full_name,
                                   titolo: "Tesserino di riconoscimento" }} />
          <BottoneRaccogli className="h-8 w-8 p-0"
                           voce={{ tipo: "generato", id: `verbale_tesserino:${emp.id}`,
                                   gruppo: emp.full_name,
                                   titolo: "Verbale di consegna del tesserino" }} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={stampa} disabled={busy}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="stampa-tesserino">
            {busy ? "Preparo..." : "Stampa"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmpForm({ initial, onSubmit, onClose }) {
  const [f, setF] = useState(initial || {
    first_name: "", last_name: "", role: "Operaio Specializzato",
    email: "", phone: "", badge_number: "", birth_date: "", hire_date: "",
    fiscal_code: "", livello: "", birth_place: "", patente_c: false, active: true,
    lavora_in_ufficio: null,
    residence_address: "", residence_city: "", residence_province: "", nationality: "",
    permesso_soggiorno: "",
  });
  const submit = (e) => {
    e.preventDefault();
    // Le righe lasciate in bianco non diventano una mansione vuota.
    onSubmit({ ...f, mansioni_extra: (f.mansioni_extra || [])
      .map((m) => String(m || "").trim()).filter(Boolean) });
  };
  // Se il dipendente ha una mansione non piu in elenco la si tiene comunque,
  // altrimenti una semplice modifica la cambierebbe senza avvisare.
  const fuoriElenco = [f.role, ...(f.mansioni_extra || [])]
    .filter((m) => m && !MANSIONI.includes(m));
  const ROLES = [...MANSIONI, ...new Set(fuoriElenco)];

  // Le altre mansioni: si aggiungono una alla volta col «+», e la prima che
  // si propone e' una che la persona non ha gia'.
  const altre = f.mansioni_extra || [];
  const cambiaAltra = (i, valore) => setF({
    ...f, mansioni_extra: altre.map((m, n) => (n === i ? valore : m)) });
  const togliAltra = (i) => setF({
    ...f, mansioni_extra: altre.filter((_, n) => n !== i) });
  // La riga nuova nasce vuota: proporre un mestiere a caso vorrebbe dire
  // ritrovarselo salvato senza averlo scelto.
  const aggiungiAltra = () => setF({ ...f, mansioni_extra: [...altre, ""] });
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{initial ? "Modifica" : "Nuovo"} dipendente</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Cognome</Label><Input value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} required /></div>
            <div><Label>Nome</Label><Input value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} required /></div>
          </div>
          <div>
            <Label>Mansione</Label>
            <div className="flex gap-2">
              <select className="flex-1 h-10 border border-slate-200 rounded px-3"
                      data-testid="mansione-principale"
                      value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
                {ROLES.map(r => <option key={r}>{r}</option>)}
              </select>
              {/* Una persona puo' saperne fare piu' d'una: il «+» aggiunge
                  un mestiere, non lo sostituisce. */}
              <Button type="button" variant="outline" className="h-10 w-10 p-0"
                      onClick={aggiungiAltra} data-testid="aggiungi-mansione"
                      title="Aggiungi un'altra mansione">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <datalist id="mansioni-note">
              {ROLES.map(r => <option key={r} value={r} />)}
            </datalist>
            {altre.length > 0 && (
              <div className="space-y-2 mt-2">
                {altre.map((m, i) => (
                  <div key={i} className="flex gap-2">
                    {/* Si sceglie dall'elenco o si scrive: i mestieri di
                        un'azienda non stanno tutti in una tendina. */}
                    <Input className="flex-1 h-10" list="mansioni-note"
                           data-testid={`mansione-extra-${i}`}
                           placeholder="Scegli o scrivi la mansione"
                           value={m} onChange={(e) => cambiaAltra(i, e.target.value)} />
                    <Button type="button" variant="outline" className="h-10 w-10 p-0"
                            onClick={() => togliAltra(i)} title="Togli questa mansione">
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-slate-500 mt-1">
              La prima è la mansione contrattuale: è quella che esce sul tesserino e
              nell&apos;elenco per i committenti. Le altre dicono cos&apos;altro sa fare, e
              valgono per i turni di reperibilità e per i filtri.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Badge</Label><Input value={f.badge_number} onChange={(e) => setF({ ...f, badge_number: e.target.value })} placeholder="opzionale" /></div>
            <div><Label>Telefono</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
          </div>
          <div><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Codice fiscale</Label>
              <Input value={f.fiscal_code || ""} maxLength={16}
                     onChange={(e) => setF({ ...f, fiscal_code: e.target.value.toUpperCase() })}
                     placeholder="RSSMRC80A01F205X" />
            </div>
            <div>
              <Label>Livello</Label>
              <Input value={f.livello || ""}
                     onChange={(e) => setF({ ...f, livello: e.target.value })}
                     placeholder="es. 4° livello" />
            </div>
          </div>
          <p className="text-xs text-slate-500 -mt-1">Compaiono nell'elenco del personale da consegnare ai committenti</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Data di nascita</Label>
              <Input type="date" value={(f.birth_date || "").slice(0, 10)}
                     onChange={(e) => setF({ ...f, birth_date: e.target.value })} />
            </div>
            <div>
              <Label>Data di assunzione</Label>
              <Input type="date" value={(f.hire_date || "").slice(0, 10)}
                     onChange={(e) => setF({ ...f, hire_date: e.target.value })} />
            </div>
          </div>
          <p className="text-xs text-slate-500 -mt-1">Entrambe obbligatorie sul tesserino di cantiere</p>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={!!f.patente_c} data-testid="patente-c"
                   onChange={(e) => setF({ ...f, patente_c: e.target.checked })} />
            Patente C: guida gli autocarri e fa lo scarico del tachigrafo
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={!!f.lavora_in_ufficio} data-testid="lavora-in-ufficio"
                   onChange={(e) => setF({ ...f, lavora_in_ufficio: e.target.checked })} />
            Lavora in ufficio: non compare fra i disponibili per i cantieri
          </label>
          <p className="text-xs text-slate-500 -mt-1">
            Chi entra nell&apos;app come amministratore, tecnico o amministrativo è già
            fuori dai cantieri per conto suo. Questa spunta serve per chi un account
            non ce l&apos;ha, e togliendola si rimette in elenco chi invece ci va.
          </p>
          <div>
            <Label>Luogo di nascita</Label>
            <Input value={f.birth_place || ""}
                   onChange={(e) => setF({ ...f, birth_place: e.target.value })}
                   placeholder="es. Milano" />
            <p className="text-xs text-slate-500 mt-1">
              Serve sul verbale di consegna del tesserino. Se manca, il verbale esce con
              la riga in bianco da completare a penna.
            </p>
          </div>
          <div>
            <Label>Indirizzo di residenza</Label>
            <Input value={f.residence_address || ""}
                   onChange={(e) => setF({ ...f, residence_address: e.target.value })}
                   placeholder="es. Via Roma 1" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label>Comune di residenza</Label>
              <Input value={f.residence_city || ""}
                     onChange={(e) => setF({ ...f, residence_city: e.target.value })} />
            </div>
            <div>
              <Label>Provincia</Label>
              <Input value={f.residence_province || ""} maxLength={2}
                     onChange={(e) => setF({ ...f, residence_province: e.target.value.toUpperCase() })}
                     placeholder="MI" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nazione</Label>
              <Input value={f.nationality || ""}
                     onChange={(e) => setF({ ...f, nationality: e.target.value })}
                     placeholder="es. Italia" />
            </div>
            <div>
              <Label>Permesso di soggiorno</Label>
              <Input value={f.permesso_soggiorno || ""}
                     onChange={(e) => setF({ ...f, permesso_soggiorno: e.target.value })}
                     placeholder="numero o scadenza" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
            <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white">Salva</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function QRDialog({ emp, onClose }) {
  const url = `${API}/employees/${emp.id}/qrcode`;
  const publicUrl = `${window.location.origin}/fascicolo/${emp.id}`;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="text-center">
        <DialogHeader><DialogTitle>QR Code · {emp.full_name}</DialogTitle></DialogHeader>
        <div className="flex flex-col items-center gap-3 p-4">
          <QRBox percorso={`/employees/${emp.id}/qrcode`} titolo={emp.full_name}
                 sottotitolo={emp.badge_number ? `matricola ${emp.badge_number}` : emp.role}
                 indirizzo={publicUrl} nomeFile={`tesserino_${emp.full_name}`} />
          <a href={url} download={`qr_${emp.badge_number}.png`}>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white">Scarica PNG</Button>
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * I DPI consegnati alla persona nell'ultimo anno, con la presa in carico.
 *
 * Sta nei documenti personali perché è lì che si va a cercare, ma non è un
 * documento caricato: si costruisce da solo dalle consegne firmate, e quindi
 * è sempre aggiornato senza che nessuno debba rifare un modulo.
 *
 * È anche quello che il coordinatore vede inquadrando il tesserino: la firma
 * è la prova che il DPI è stato preso in carico, e senza quella l'elenco
 * sarebbe solo una nostra affermazione.
 */
function ElencoDPI({ emp }) {
  const empId = emp.id;
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState(false);
  // Chiusa di partenza: nei documenti personali si entra per un'altra ragione,
  // e un elenco lungo aperto d'ufficio spingerebbe giù tutto il resto.
  const [aperta, setAperta] = useState(false);
  const [scarico, setScarico] = useState(false);

  const scarica = async () => {
    setScarico(true);
    try {
      const r = await authFetch(`/employees/${empId}/dpi/pdf`);
      if (!r.ok) throw new Error();
      const cd = r.headers.get("content-disposition") || "";
      const nome = /filename="?([^";]+)"?/.exec(cd)?.[1]
        || `Elenco_DPI_${emp.full_name}.pdf`;
      const u = window.URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = u; a.download = nome; a.click();
      window.URL.revokeObjectURL(u);
    } catch { toast.error("Errore nella stampa dell'elenco"); }
    finally { setScarico(false); }
  };

  useEffect(() => {
    let vivo = true;
    api.get(`/employees/${empId}/dpi`)
      .then(({ data }) => { if (vivo) setDati(data); })
      .catch(() => { if (vivo) setErrore(true); });
    return () => { vivo = false; };
  }, [empId]);

  if (errore) return null;
  if (!dati) {
    return <p className="text-xs text-slate-400 py-2">Caricamento consegne...</p>;
  }

  const giorno = (iso) => (iso ? iso.substring(0, 10).split("-").reverse().join("/") : "");

  return (
    <div className="border border-slate-200 rounded-lg" data-testid="elenco-dpi-fascicolo">
      <button type="button" onClick={() => setAperta((a) => !a)}
              data-testid="tendina-dpi"
              className="w-full p-3 flex items-center gap-2 text-left">
        <HardHat className="w-4 h-4 text-orange-500 flex-shrink-0" />
        <span className="text-sm font-semibold">DPI ultimo anno</span>
        <span className="text-xs text-slate-500">
          {dati.consegne.length}{" "}
          {dati.consegne.length === 1 ? "consegna" : "consegne"}
          {dati.capi > 0 && ` · ${dati.capi} capi`}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 ml-auto flex-shrink-0 transition-transform ${
          aperta ? "rotate-180" : ""}`} />
      </button>
      {/* Non e' un documento caricato, ma si porta via come gli altri: il
          foglio con le firme di presa in carico e' quello che il
          coordinatore chiede in sopralluogo. */}
      <div className="flex items-center gap-1.5 px-3 pb-2 -mt-1">
        <Button variant="outline" size="sm" className="h-8"
                onClick={scarica} disabled={scarico}
                data-testid="scarica-elenco-dpi">
          <Download className="w-3 h-3 mr-1" />
          {scarico ? "Preparo il PDF..." : "Scarica l'elenco"}
        </Button>
        <BottoneRaccogli className="h-8 w-8 p-0"
                         voce={{ tipo: "elenco_dpi", id: empId, gruppo: emp.full_name,
                                 titolo: "Elenco DPI consegnati",
                                 sottotitolo: "ultimo anno, con le firme" }} />
      </div>
      {aperta && (dati.consegne.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-6 border-t border-slate-100">
          Nessuna consegna nell&apos;ultimo anno.
        </p>
      ) : (
        <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 border-t border-slate-100">
          {dati.consegne.map((c) => (
            <div key={c.id} className="p-3 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold tabular-nums">{giorno(c.data)}</div>
                <ul className="mt-0.5">
                  {c.righe.map((r, i) => (
                    <li key={i} className="text-sm text-slate-700">
                      {r.quantita > 1 && <span className="tabular-nums">{r.quantita}× </span>}
                      {r.articolo}
                      {r.taglia && <span className="text-slate-400"> · taglia {r.taglia}</span>}
                    </li>
                  ))}
                </ul>
                {c.consegnato_da && (
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    consegnati da {c.consegnato_da}
                  </div>
                )}
              </div>
              <div className="flex-shrink-0 text-center w-28">
                {c.firma ? (
                  <>
                    <img src={c.firma} alt="firma di presa in carico"
                         className="h-10 w-28 object-contain bg-white border border-slate-200 rounded" />
                    <div className="text-[10px] text-emerald-600 mt-0.5 flex items-center justify-center gap-1">
                      <PenLine className="w-3 h-3" /> presa in carico
                    </div>
                  </>
                ) : (
                  <div className="text-[10px] text-slate-400">
                    storico cartaceo, senza firma digitale
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// La scheda SGM M8.1.3.1.4 come la chiama l'indirizzo delle Schede e IO.
const SLUG_DUE_DILIGENCE = "sgm-m8-1-3-1-4";

/* ---------- FASCICOLO: due sezioni distinte ---------- */
export function FascicoloDialog({ emp, onClose }) {
  const { user } = useAuth();
  // Le due specie di documento non si toccano allo stesso modo. L'attestato lo
  // carica anche il tecnico: è lui che riceve il certificato del corso. Carta
  // d'identità, patente, UNILAV e idoneità sanitaria restano a direzione e
  // amministrazione, perché sono dati personali e uno di quelli è sanitario.
  const puoAttestati = ["admin", "amministrativo", "tecnico"].includes(user?.role);
  const puoPersonali = ["admin", "amministrativo"].includes(user?.role);
  const puoSu = (d) => (classOf(d) === "personale" ? puoPersonali : puoAttestati);
  const [docs, setDocs] = useState([]);
  const [addMode, setAddMode] = useState(null); // "personale" | "attestato" | null

  const load = async () => {
    try {
      const { data } = await api.get(`/certificates?employee_id=${emp.id}`);
      setDocs(data);
    } catch { toast.error("Errore caricamento fascicolo"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [emp.id]);

  const onDel = async (id) => {
    if (!window.confirm("Eliminare definitivamente questo documento?")) return;
    await api.delete(`/certificates/${id}`); load();
  };
  const onArchive = async (id) => { await api.post(`/certificates/${id}/archive`); load(); };
  const [editDoc, setEditDoc] = useState(null);
  const onRestore = async (id) => { await api.post(`/certificates/${id}/restore`); load(); };

  const active = docs.filter(d => !d.archived);
  const archived = docs.filter(d => d.archived);
  const personali = active.filter(d => classOf(d) === "personale");
  const attestati = active.filter(d => classOf(d) === "attestato");

  // Come questo documento si presenta dentro il raccoglitore: il nome della
  // persona diventa la cartella, cosi' un pacchetto di tre dipendenti resta
  // diviso per dipendente anche sul computer di chi lo riceve.
  const voceDi = (d) => (d.file_url ? {
    tipo: "dipendente", id: d.id, gruppo: emp.full_name,
    titolo: d.title || d.category,
    sottotitolo: [d.category !== d.title ? d.category : null,
                  d.expires_date ? `scade il ${(d.expires_date || "").substring(0, 10).split("-").reverse().join("/")}` : null]
      .filter(Boolean).join(" · "),
  } : null);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Fascicolo · {emp.full_name}
            <Badge variant="outline" className="font-normal">{emp.badge_number}</Badge>
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="personali">
          <TabsList className="w-full justify-start overflow-x-auto">
            <TabsTrigger value="personali" data-testid="tab-personali">
              <IdCard className="w-4 h-4 mr-1" /> Documenti personali ({personali.length})
            </TabsTrigger>
            <TabsTrigger value="attestati" data-testid="tab-attestati">
              <GraduationCap className="w-4 h-4 mr-1" /> Attestati ({attestati.length})
            </TabsTrigger>
            <TabsTrigger value="archivio">
              <Archive className="w-4 h-4 mr-1" /> Archivio ({archived.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="personali" className="space-y-1.5">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-xs text-slate-500 flex-1 min-w-[200px]">
                Carta d&apos;identità, codice fiscale, patente, UNILAV, idoneità
                sanitaria. In fondo, l&apos;elenco dei DPI consegnati.
              </p>
              {/* La verifica che la ISO 37001 chiede prima di assumere. Si
                  compila nell'app, non si scarica: si parte da qui perche' la
                  persona e' questa, e l'anagrafica entra da sola. */}
              {puoPersonali && (
                <Link to={`/sistemi-integrati?scheda=schede&compila=${SLUG_DUE_DILIGENCE}&persona=${emp.id}`}
                      data-testid="due-diligence-dipendente">
                  <Button size="sm" variant="outline" className="h-8">
                    <ScrollText className="w-3 h-3 mr-1" /> Due diligence
                  </Button>
                </Link>
              )}
              {puoPersonali && (
                <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white h-8"
                        onClick={() => setAddMode("personale")}>
                  <Upload className="w-3 h-3 mr-1" /> Carica documento
                </Button>
              )}
            </div>
            {personali.length === 0
              ? <p className="text-sm text-slate-500 text-center py-4">Nessun documento personale caricato</p>
              : personali.map(d => <DocRow key={d.id} d={d} isAdmin={puoPersonali} voce={puoPersonali ? voceDi(d) : null}
                                           onArchive={onArchive} onDel={onDel} onEdit={setEditDoc} />)}

            {/* Non è un documento caricato: si costruisce dalle consegne
                firmate, quindi è sempre aggiornato da solo. */}
            <ElencoDPI emp={emp} />
          </TabsContent>

          <TabsContent value="attestati" className="space-y-1.5">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-xs text-slate-500 flex-1 min-w-[200px]">
                Attestati di formazione: antincendio, primo soccorso, macchine movimento terra, PLE, preposti.
              </p>
              {/* Il pacchetto formazione: quello che si manda quando un
                  committente chiede «mandami i corsi di Tizio». */}
              <BottoneRaccogliTutti voci={attestati.map(voceDi).filter(Boolean)}
                                    etichetta="Pacchetto formazione" />
              {puoAttestati && (
                <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white h-8"
                        onClick={() => setAddMode("attestato")}>
                  <Upload className="w-3 h-3 mr-1" /> Carica attestato
                </Button>
              )}
            </div>
            {attestati.length === 0
              ? <p className="text-sm text-slate-500 text-center py-4">Nessun attestato caricato</p>
              : attestati.map(d => <DocRow key={d.id} d={d} isAdmin={puoAttestati} voce={voceDi(d)}
                                           onArchive={onArchive} onDel={onDel} onEdit={setEditDoc} />)}
          </TabsContent>

          <TabsContent value="archivio" className="space-y-1.5">
            <p className="text-xs text-slate-500">Documenti superati o scaduti, conservati per tracciabilità.</p>
            {archived.length === 0
              ? <p className="text-sm text-slate-500 text-center py-4">Archivio vuoto</p>
              : archived.map(d => <DocRow key={d.id} d={d} isAdmin={puoSu(d)} voce={puoSu(d) ? voceDi(d) : null}
                                          archivedView onRestore={onRestore} onDel={onDel} />)}
          </TabsContent>
        </Tabs>

        {addMode && (addMode === "personale" ? puoPersonali : puoAttestati) && (
          <AddDocDialog emp={emp} mode={addMode}
                        onClose={() => setAddMode(null)}
                        onSaved={() => { setAddMode(null); load(); }} />
        )}

        {editDoc && puoSu(editDoc) && (
          <EditDocDialog doc={editDoc}
                         onClose={() => setEditDoc(null)}
                         onSaved={() => { setEditDoc(null); load(); }} />
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Correzione dei dati di un documento già caricato. Il file resta quello:
 * serve a rimediare a ore sbagliate, categoria approssimativa o scadenza da
 * rettificare, senza dover eliminare e ricaricare perdendo l'allegato.
 */
function EditDocDialog({ doc, onClose, onSaved }) {
  const personale = classOf(doc) === "personale";
  const [f, setF] = useState({
    category: doc.category || "",
    title: doc.title || "",
    issuer: doc.issuer || "",
    issued_date: (doc.issued_date || "").substring(0, 10),
    expires_date: (doc.expires_date || "").substring(0, 10),
    tipologia: doc.tipologia || "",
    hours: doc.hours ?? "",
    notes: doc.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const tipi = personale ? PERSONAL_DOC_TYPES : COURSE_TYPES;

  const salva = async () => {
    if (f.expires_date && f.issued_date && f.expires_date < f.issued_date) {
      toast.error("La scadenza non può precedere l'emissione");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/certificates/${doc.id}`, {
        ...f,
        hours: f.hours === "" ? null : Number(f.hours),
        issuer: f.issuer || null, notes: f.notes || null,
        tipologia: f.tipologia || null,
        issued_date: f.issued_date || null, expires_date: f.expires_date || null,
      });
      toast.success("Documento aggiornato");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Modifica documento</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Il file allegato non cambia. Per sostituirlo occorre caricarne uno nuovo.
          </p>
          <div><Label>Tipo</Label>
            <select className="w-full h-10 border border-slate-200 rounded px-3"
                    value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}
                    data-testid="edit-doc-category">
              {[...tipi, ...(f.category && !tipi.includes(f.category) ? [f.category] : [])]
                .map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div><Label>Titolo</Label>
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></div>
          {!personale && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tipologia</Label>
                <select className="w-full h-10 border border-slate-200 rounded px-3"
                        value={f.tipologia} onChange={(e) => setF({ ...f, tipologia: e.target.value })}>
                  <option value="">—</option>
                  {TIPOLOGIE.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div><Label>Ore</Label>
                <Input type="number" min="0" value={f.hours} data-testid="edit-doc-hours"
                       onChange={(e) => setF({ ...f, hours: e.target.value })} /></div>
            </div>
          )}
          <div><Label>Ente / rilasciato da</Label>
            <Input value={f.issuer} onChange={(e) => setF({ ...f, issuer: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Data emissione</Label>
              <Input type="date" value={f.issued_date}
                     onChange={(e) => setF({ ...f, issued_date: e.target.value })} /></div>
            <div><Label>Data scadenza</Label>
              <Input type="date" value={f.expires_date}
                     onChange={(e) => setF({ ...f, expires_date: e.target.value })} /></div>
          </div>
          <div><Label>Note</Label>
            <Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={salva} disabled={busy} data-testid="edit-doc-save"
                  className="bg-orange-500 hover:bg-orange-600 text-white">
            {busy ? "Salvataggio..." : "Salva modifiche"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DocRow({ d, isAdmin, archivedView, onArchive, onRestore, onDel, onEdit, voce }) {
  const dd = dayDiff(d.expires_date);
  const expired = dd !== null && dd < 0;
  const expiring = dd !== null && dd >= 0 && dd <= 60;
  // Una riga sola per documento: titolo, etichette e date in linea, i
  // pulsanti a destra. Il fascicolo di trent'anni sta in una schermata.
  const data_it = (iso) => (iso ? iso.substring(0, 10).split("-").reverse().join("/") : "");
  const bottone = "h-7 w-7 p-0";
  return (
    <div className={`px-2.5 py-1.5 rounded border flex items-center gap-2
                     ${expired ? "border-red-200 bg-red-50" : expiring ? "border-amber-200 bg-amber-50" : "border-slate-200"}`}>
      <FileText className="w-4 h-4 text-orange-500 flex-shrink-0" />
      <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="font-semibold text-sm truncate max-w-full">{d.title || d.category}</span>
        {d.category !== d.title && <Badge variant="outline" className="text-[10px] py-0">{d.category}</Badge>}
        {d.tipologia && <Badge className="bg-slate-100 text-slate-700 border-0 text-[10px] py-0">{d.tipologia}</Badge>}
        {d.hours ? <Badge className="bg-slate-100 text-slate-700 border-0 text-[10px] py-0">
          <Clock className="w-3 h-3 mr-1" />{d.hours}h</Badge> : null}
        {d.issuer && <Badge className="bg-slate-100 text-slate-700 border-0 text-[10px] py-0">
          <Building2 className="w-3 h-3 mr-1" />{d.issuer}</Badge>}
        <span className="text-xs text-slate-500 whitespace-nowrap">
          {d.issued_date && <>{data_it(d.issued_date)}</>}
          {d.expires_date && <> → {data_it(d.expires_date)}</>}
          {expired && <span className="text-red-600 font-semibold"> · SCADUTO</span>}
          {expiring && <span className="text-amber-700 font-semibold"> · fra {dd} gg</span>}
        </span>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {d.file_url && voce && <BottoneRaccogli voce={voce} className={bottone} />}
        {d.file_url && (
          <a href={fileUrl(d.file_url)} target="_blank" rel="noopener noreferrer" title="Apri">
            <Button variant="outline" size="sm" className={bottone}><ExternalLink className="w-3.5 h-3.5" /></Button>
          </a>
        )}
        {isAdmin && onEdit && (
          <Button variant="outline" size="sm" className={bottone} onClick={() => onEdit(d)} title="Modifica i dati"
                  data-testid={`edit-doc-${d.id}`}>
            <Edit className="w-3 h-3" />
          </Button>
        )}
        {isAdmin && !archivedView && (
          <Button variant="outline" size="sm" className={bottone} onClick={() => onArchive(d.id)} title="Archivia">
            <Archive className="w-3 h-3" />
          </Button>
        )}
        {isAdmin && archivedView && (
          <Button variant="outline" size="sm" className={bottone} onClick={() => onRestore(d.id)} title="Ripristina">
            <Upload className="w-3 h-3" />
          </Button>
        )}
        {isAdmin && (
          <Button variant="outline" size="sm" className={bottone} onClick={() => onDel(d.id)} title="Elimina">
            <Trash2 className="w-3 h-3 text-red-500" />
          </Button>
        )}
      </div>
    </div>
  );
}

function AddDocDialog({ emp, mode, onClose, onSaved }) {
  const isCourse = mode === "attestato";
  const options = isCourse ? COURSE_TYPES : PERSONAL_DOC_TYPES;
  const [f, setF] = useState({
    category: options[0],
    title: "", issuer: "", issued_date: "", expires_date: "",
    tipologia: isCourse ? "Prima emissione" : "",
    hours: "", notes: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const fd = new FormData();
    fd.append("employee_id", emp.id);
    fd.append("doc_class", mode);
    Object.entries(f).forEach(([k, v]) => { if (v) fd.append(k, v); });
    if (!f.title) fd.set("title", f.category);
    if (file) fd.append("file", file);
    try {
      await api.post("/certificates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(isCourse ? "Attestato caricato" : "Documento caricato");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore nel caricamento"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isCourse ? "Nuovo attestato" : "Nuovo documento personale"} · {emp.full_name}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>{isCourse ? "Tipo di corso *" : "Tipo di documento *"}</Label>
            <select className="w-full h-10 border border-slate-200 rounded px-3"
                    value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
              {options.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>

          {isCourse && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipologia</Label>
                <select className="w-full h-10 border border-slate-200 rounded px-3"
                        value={f.tipologia} onChange={(e) => setF({ ...f, tipologia: e.target.value })}>
                  {TIPOLOGIE.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label>Ore di formazione</Label>
                <Input type="number" min="0" list="ore-preset" placeholder="es. 8"
                       value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} />
                <datalist id="ore-preset">
                  {ORE_PRESET.map(o => <option key={o} value={o} />)}
                </datalist>
              </div>
            </div>
          )}

          <div>
            <Label>{isCourse ? "Ente formatore" : "Ente / autorità di rilascio"}</Label>
            <Input value={f.issuer} onChange={(e) => setF({ ...f, issuer: e.target.value })}
                   placeholder={isCourse ? "es. AIFOS, INAIL, CFP" : "es. Comune, Prefettura"} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Data emissione</Label>
              <Input type="date" value={f.issued_date} onChange={(e) => setF({ ...f, issued_date: e.target.value })} /></div>
            <div><Label>Data scadenza</Label>
              <Input type="date" value={f.expires_date} onChange={(e) => setF({ ...f, expires_date: e.target.value })} /></div>
          </div>

          <div>
            <Label>Titolo personalizzato</Label>
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}
                   placeholder="opzionale — se vuoto usa il tipo selezionato" />
          </div>

          <div>
            <Label>File (PDF o immagine)</Label>
            <Input type="file" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files[0])} />
          </div>

          <div><Label>Note</Label>
            <Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
            <Button type="submit" disabled={busy}
                    className="bg-orange-500 hover:bg-orange-600 text-white">
              {busy ? "Caricamento..." : "Carica"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
